import React, { useEffect, useState } from "react";
import { getMapsStatic, MapsStaticParams } from "../../services/maps";
import {
  canUseMapsStaticBff,
  parseStaticMapQuery,
} from "../../utils/maps/parse-static-map-query";

type MapsStaticImageProps = {
  /** Legacy Google Static Map query or full URL (with or without key). */
  query?: string;
  lat?: number;
  lng?: number;
  zoom?: number;
  width?: number;
  height?: number;
  maptype?: MapsStaticParams["maptype"];
  path?: string | string[];
  markers?: string | string[];
  alt?: string;
  className?: string;
  style?: React.CSSProperties;
};

function toList(value?: string | string[]): string[] {
  if (!value) return [];
  return (Array.isArray(value) ? value : [value]).filter(Boolean);
}

/**
 * Renders a static map via authenticated BFF.
 * Overlays (path/markers) and URL signature are handled on the backend.
 */
export default function MapsStaticImage(props: MapsStaticImageProps) {
  const {
    query,
    lat: latProp,
    lng: lngProp,
    zoom: zoomProp,
    width: widthProp,
    height: heightProp,
    maptype: maptypeProp,
    path: pathProp,
    markers: markersProp,
    alt = "",
    className,
    style,
  } = props;

  const [src, setSrc] = useState<string | undefined>(undefined);

  useEffect(() => {
    let objectUrl: string | undefined;
    let cancelled = false;

    const parsed = query ? parseStaticMapQuery(query) : null;
    const lat = latProp ?? parsed?.lat;
    const lng = lngProp ?? parsed?.lng;
    const zoom = zoomProp ?? parsed?.zoom ?? 16;
    const width = widthProp ?? parsed?.width ?? 320;
    const height = heightProp ?? parsed?.height ?? 320;
    const maptype = maptypeProp ?? parsed?.maptype ?? "satellite";
    const path = [...toList(pathProp), ...(parsed?.path ?? [])];
    const markers = [...toList(markersProp), ...(parsed?.markers ?? [])];

    if (
      lat === undefined ||
      lng === undefined ||
      (query && !canUseMapsStaticBff(parsed!))
    ) {
      setSrc(undefined);
      return () => {
        cancelled = true;
      };
    }

    const bffParams: MapsStaticParams = {
      lat,
      lng,
      zoom,
      width,
      height,
      maptype,
      ...(path.length ? { path } : {}),
      ...(markers.length ? { markers } : {}),
    };

    getMapsStatic(bffParams)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      })
      .catch(() => {
        if (cancelled) return;
        setSrc(undefined);
      });

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [
    query,
    latProp,
    lngProp,
    zoomProp,
    widthProp,
    heightProp,
    maptypeProp,
    pathProp,
    markersProp,
  ]);

  if (!src) {
    return (
      <div
        className={className}
        style={{
          background: "#00000011",
          minWidth: widthProp || 90,
          minHeight: heightProp || 90,
          ...style,
        }}
      />
    );
  }

  return <img src={src} alt={alt} className={className} style={style} />;
}
